import Person from "@/types/Person.interface"
import User from "@/types/User.interface"
import { PersonParams } from "@/types/PaginationParams.interface"
import Pagination from "@/types/Pagination.interface"
import { GroupQuery } from "@/types/Group.interface"

interface State {
    contactsPersons: Person[]                   //  The channels displayed in the Inbox
    contactsPersonsCurrentPage: number          //  The current page of channels pagination (with current filter)
    contactsPersonsLastPage: number             //  The last page of current paginate (with current filter)
    contactsPersonsNextPage: number             //  The 'next' page of pagination (with current filter)
    contactsPersonsParams: PersonParams         //  The contacts' current channel pagination params
    contactsPersonsQueryFilter: GroupQuery      //  The current filter for our persons when using query filtering
    contactsPaginating: boolean                 //  Indicates contactsPersons pagination is in progress
    contactsSearchedPersons: Person[]           // The list of persons we searched for
    contactsSelectedPerson: Person              //  The contacts' currently selected channel
}

//Param thats entered into our contactsPersons state and contains it's contructor details 
const defaults = () => {
    return {
        contactsPersons: [],
        contactsPersonsCurrentPage: null,
        contactsPersonsLastPage: null,
        contactsPersonsNextPage: null,
        contactsPersonsParams: {
            page: 1
        },
        contactsPersonsQueryFilter: null,
        contactsPaginating: false,
        contactsSearchedPersons: null,
        contactsSelectedPerson: null,
    }
}

export default {
    //Where our contactsPersons state gets defined 
    state: defaults(),
    //The mutations that affect the contactsPersons state such as removing a person from the state or adding a person 
    mutations: {
        //  PERSONS
        contactsPersonsParamsSet(state: State, params: PersonParams): void {
            state.contactsPersonsParams = params
        },
        contactsPersonsPush(state: State, persons: Person[]): void {
            //  Update the state
            state.contactsPersons = window.removeDuplicatesById(state.contactsPersons, persons)
        },
        contactsPersonsQueryFilterSet (state: State, query: GroupQuery): void {
            state.contactsPersonsQueryFilter = query
        },
        contactsPersonsRemove(state: State, ids: number[]): void {
            //  Get our current list of persons
            const persons = state.contactsPersons

            //  If no persons, do nothing
            if (!persons) return

            //  Else, filter our our removed ids
            const filtered = persons.filter(x => !ids.includes(x.id))

            //  Set our persons to the filtered list
            state.contactsPersons = filtered

            //  Get the currently selected person
            const selectedPerson = state.contactsSelectedPerson

            //  If the person does NOT exist, we have nothing to do
            if (!selectedPerson) return

            //  Else, find if our person was deleted
            const deleted = ids.find(x => x == selectedPerson.id)

            //  If the selected person was NOT deleted, we have nothing to do
            if (!deleted) return

            //  Else, update the selected person to null since deleted
            state.contactsSelectedPerson = null
        },
        contactsPersonsSync(state: State, updates: Person[]):void {
            //  Get our list of persons
            const persons = state.contactsPersons

            //  If no persons, do nothing
            if (!persons) return

            //  Grab all the ids from our persons
            const ids = persons.map((x: Person) => x.id)

            //  If we have a query filter set, then sync, else, we push
            const filterSet = state.contactsPersonsQueryFilter !== null

            //  TODO:  This needs to be updated to apply our query filter
            //  I think for now leaving it is fine.
            //  Current behavior is that it will ALWAYS add a contact to the list, which isn't an issue unless filtering or searching
            //  Else, filter out our updates to ONLY contain items that are in the persons list
            const filtered = filterSet ? updates.filter((x: Person) => ids.includes(x.id)) : updates

            //  Find which persons we will have to remove from this list based on filtered
            const remove = window._.differenceBy(updates, filtered, 'id')

            //  Update our list with the filtered array
            state.contactsPersons = window.removeDuplicatesById(state.contactsPersons, filtered, remove)

            //  Check if we have a selected person
            const selectedPerson = state.contactsSelectedPerson

            //  If it doesn't exist, do nothing
            if (!selectedPerson) return

            //  Else, check if our filtered contains this ids
            const updatedPerson = updates.find((x: Person) => x.id == selectedPerson.id)

            //  If it does NOT contain the id, do nothing
            if (!updatedPerson) return

            //  Else, update our selected person
            state.contactsSelectedPerson = updatedPerson
        },
        contactsPaginationPush(state: State, page: Pagination): void {
            //  If this is page one, reset the the contacts list
            if (page.current_page === 1) state.contactsPersons = []

            state.contactsPersonsCurrentPage = page.current_page
            state.contactsPersonsLastPage = page.last_page
            state.contactsPersonsNextPage = page.current_page + 1
        },
        contactsPaginatingSet(state: State, bool: boolean): void {
            state.contactsPaginating = bool
        },
        //  SEARCHED PERSONS
        contactsSearchedPersonsRemove (state: State, ids: number[]): void {
            //  Get our list of persons
            const persons = state.contactsSearchedPersons

            //  If no persons, do nothing
            if (!persons) return

            //  Else, filter out our removed ids
            const filtered = persons.filter((x: Person) => !ids.includes(x.id))

            //  Replace our list with the filtered
            state.contactsSearchedPersons = filtered
        },
        contactsSearchedPersonsSet (state: State, persons: Person[]): void {
            state.contactsSearchedPersons = persons
        },
        contactsSearchedPersonsSync (state: State, updates: Person[]): void {
            //  Get our list of persons
            const persons = state.contactsSearchedPersons

            //  If no persons, do nothing
            if (!persons) return

            //  Update our list with the filtered array
            state.contactsSearchedPersons = window.removeDuplicatesById(state.contactsSearchedPersons, updates)
        },
        //  SELECTED PERSON
        contactsSelectedPersonSet(state: State, person: Person): void {
            state.contactsSelectedPerson = person
        },
        contactsSelectedPersonSync(state: State, persons: Person[]): void {
            //  Grab our currently selected person
            const selectedPerson = state.contactsSelectedPerson

            //  If we don't have a selected person, we are done
            if (!selectedPerson) return

            //  Find if our selected person is in the updates
            const updatedPerson = persons.find((x: Person) => x.id === selectedPerson.id)

            //  If we have not updated this person, do nothing
            if (!updatedPerson) return

            //  Else, replace our person
            state.contactsSelectedPerson = updatedPerson
        },
        contactsReset(state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        contactsPersonsGetQuery({commit, getters}, query) {
            //  Start the 'paginating' tracker
            commit('contactsPaginatingSet', true)

            //  Assemble the api endpoint
            const url = `${getters.api}/persons/query`

            //  Send the request
            return window.axios.post(url, query).then(response => {
                
                //  Update paginationg params (this must occur BEFORE updasting the persons list, for reset to work properly)
                commit('contactsPaginationPush', response.data)

                //  Update the contacts persons list
                commit('contactsPersonsPush', response.data.data)

                //  Stop the 'paginating' tracker
                commit('contactsPaginatingSet', false)

                //  Return the pagination data
                return response.data
            }).catch(() => null)
        },
        contactsPersonsPaginate({ commit, getters }): Promise<Pagination> {
            //  Select our pagination params (if no input, use default params)
            const p = getters.contactsPersonsParams

            //  Start the 'paginating' tracker
            commit('contactsPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/persons?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {

                //  Update pagination params (this must occur BEFORE updating the persons list, for reset to work properly)
                commit('contactsPaginationPush', response.data)

                //  Update the contacts channels list
                commit('contactsPersonsPush', response.data.data)

                //  Stop the 'paginating' tracker
                commit('contactsPaginatingSet', false)

                //  Return the pagination data
                return response.data
            }).catch(() => {
                return null
            })
        },
        //  Used when mass changes happen that we cannot use wherein on
        contactsPersonsRefresh ({ commit, dispatch, getters }) {
            //  Grab our pagination params
            const params = getters.contactsPersonsParams

            //  Set page to 1
            params.page = 1

            //  Commit the changes
            commit('contactsPersonsParamsSet', params)

            //  Paginate
            dispatch('contactsPersonsPaginate')
        },
        //  1. HTTP request of type GET
        //  2. The Person resource is modified in line 215 ("commit('contactsSelectedPersonSet', response.data)")
        //  3. Running this action again (searching for user) will result in these actions not to be executed 
        //     and instead the persons ID would be loaded from the mounted() function in ContactsIndex.vue
        contactsSelectedPersonGet({ commit, getters }, personId: number) {
            //  Get the global persons list
            const persons = getters.persons

            //  Search for the exsiting person in the global dataset
            const existingPerson = persons.find(x => x.id == personId)

            console.log('contactsSelectedPersonGet', personId, existingPerson)
            //  If we found an existing person AND they have all lazy attributes, return it
            if (existingPerson) return commit('contactsSelectedPersonSet', existingPerson)

            //  Start the 'paginating' tracker
            commit('contactsPaginatingSet', true)

            //  Else, assemble the API endpoint
            const url = `${getters.api}/persons/${personId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set the inbox selected channel
                commit('contactsSelectedPersonSet', response.data)

                //  Add the channel to the global dataset
                commit('personsPush', [response.data])

                //  Stop the 'paginating' tracker
                commit('contactsPaginatingSet', false)

                return response.data
            }).catch(() => null)
        }
    },
    //The getters that get information about the current state that contactsPersons exits in 
    getters: {
        contactsPersons (state: State): Array<Person> {
            return state.contactsPersons
        },
        contactsPersonsLastPage (state: State): number {
            return state.contactsPersonsLastPage
        },
        contactsPersonsNextPage (state: State): number {
            return state.contactsPersonsNextPage
        },
        contactsPersonsParams (state: State): PersonParams {
            return state.contactsPersonsParams
        },
        contactsPersonsQueryFilter (state: State): object {
            return state.contactsPersonsQueryFilter
        },
        contactsPaginating (state: State): boolean {
            return state.contactsPaginating
        },
        contactsSearchedPersons (state: State): Person[] {
            return state.contactsSearchedPersons
        },
        contactsSelectedPerson (state: State): Person {
            return state.contactsSelectedPerson
        }
    }
}